/**
 * Empty state for Key Findings — intentionally quiet; the section H2
 * already labels the block so we do not repeat the title here.
 */
import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

export interface KeyFindingsEmptyStateProps {
  message: string;
  isAdmin?: boolean;
  onRegenerate?: (sectionId: string) => Promise<void> | void;
  exportMode?: boolean;
}

export const KeyFindingsEmptyState: React.FC<KeyFindingsEmptyStateProps> = ({
  message,
  isAdmin,
  onRegenerate,
  exportMode = false,
}) => {
  const [regenerating, setRegenerating] = React.useState(false);
  const showRegenerate =
    !exportMode && isAdmin === true && typeof onRegenerate === 'function';

  const handleRegenerate = async () => {
    if (regenerating || !onRegenerate) return;
    setRegenerating(true);
    try {
      await onRegenerate('projectIntentOverview');
    } catch {
      // Parent toasts failure; keep placeholder mounted.
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      className="flex flex-1 flex-col justify-center gap-3 min-h-[200px] lg:min-h-[260px]"
      data-testid="key-findings-empty"
    >
      <p className="text-sm leading-relaxed text-text-secondary m-0 max-w-prose">
        {message}
      </p>
      {showRegenerate && (
        <div>
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerating}
            data-testid="key-findings-regenerate"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-1 px-3 py-1.5 text-xs font-medium text-text-primary hover:bg-surface-2 disabled:opacity-60"
          >
            {regenerating ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" aria-hidden />
            )}
            {regenerating ? 'Regenerating…' : 'Regenerate Key Findings'}
          </button>
        </div>
      )}
    </div>
  );
};
